import { Loader2, Maximize, Minimize } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ConfirmDeleteDialogProps {
  visible: boolean;
  title: string;
  message: string;
  isDeleting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDeleteDialog: FC<ConfirmDeleteDialogProps> = ({
  visible,
  title,
  message,
  isDeleting = false,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const dialogHeader = (
    <div className="flex items-center justify-between w-full pr-2">
      <span className="font-semibold text-lg">{title}</span>
      <Button
        icon={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
        onClick={() => setIsFullscreen((prev) => !prev)}
        text={true}
        rounded={true}
        size="small"
        title={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltip={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        tooltipOptions={{ position: 'left' }}
        aria-label={isFullscreen ? t('dialogs.exitFullscreen') : t('dialogs.enterFullscreen')}
        pt={{
          root: {
            style: { outline: 'none', boxShadow: 'none' },
            className: 'focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none',
          },
        }}
      />
    </div>
  );

  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      onHide={isDeleting ? () => {} : onCancel}
      closable={!isDeleting}
      modal={true}
      className={`w-full ${isFullscreen ? 'max-w-full h-full' : 'max-w-md'}`}
      style={
        isFullscreen ? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', margin: 0 } : undefined
      }
      pt={{
        root: { className: isFullscreen ? 'h-full fixed inset-0' : '' },
        content: { className: isFullscreen ? 'h-full flex flex-col' : '' },
      }}
    >
      <div className="flex flex-col gap-6">
        <p className="text-sm text-gray-600">{message}</p>

        {isDeleting && (
          <div className="flex items-center gap-3 text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
            <Loader2 className="w-4 h-4 animate-spin text-red-500 flex-shrink-0" />
            <span>{t('dialogs.deletingInProgress', 'Mazání probíhá, prosím vyčkejte…')}</span>
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button label={t('dialogs.cancel', 'Cancel')} onClick={onCancel} outlined={true} disabled={isDeleting} />
          <Button
            label={t('dialogs.delete', 'Delete')}
            onClick={onConfirm}
            severity="danger"
            disabled={isDeleting}
            loading={isDeleting}
          />
        </div>
      </div>
    </Dialog>
  );
};
